import type { MonitorResponse, QueueOverview, QueueRunDetail } from "./types";

/** Async read responses can arrive after a newer revision has been rendered. */
export function retainNewerEvidence<T extends MonitorResponse>(
  previous: T | undefined,
  next: T,
): T {
  if (
    !previous ||
    previous.environment !== next.environment ||
    previous.projection_generation !== next.projection_generation
  )
    return next;
  if ("run" in next && "run" in previous) {
    const before = previous as unknown as QueueRunDetail;
    const after = next as unknown as QueueRunDetail;
    if (
      before.run.workload_id === after.run.workload_id &&
      before.run.revision > after.run.revision
    )
      return previous;
  }
  if ("active" in next && "active" in previous) {
    const before = previous as unknown as QueueOverview;
    const after = next as unknown as QueueOverview;
    const known = new Map(
      [...before.active.items, ...before.recent_exits].map((run) => [
        run.workload_id,
        run,
      ]),
    );
    const fresh = (run: QueueOverview["recent_exits"][number]) => {
      const old = known.get(run.workload_id);
      return old && old.revision > run.revision ? old : run;
    };
    const active = after.active.items.map(fresh);
    const recent = new Map(
      [
        ...after.recent_exits.map(fresh),
        ...active.filter((run) => run.lifecycle !== "active"),
      ].map((run) => [run.workload_id, run]),
    );
    return {
      ...after,
      active: {
        ...after.active,
        items: active.filter((run) => run.lifecycle === "active"),
      },
      recent_exits: [...recent.values()]
        .filter((run) => run.lifecycle !== "active")
        .sort(
          (a, b) =>
            (b.terminal_at ?? "").localeCompare(a.terminal_at ?? "") ||
            b.workload_id.localeCompare(a.workload_id),
        )
        .slice(0, 5),
    } as unknown as T;
  }
  return next;
}
